
import { useState } from 'react';

const faqs = [
  {
    question: 'Where do the products go?',
    answer:
      'All oxygen supplementation products are distributed directly to underprivileged families in rural and semi-urban communities across India. We work with local health workers and partner NGOs to identify families with the greatest need and ensure products reach them safely.',
  },
  {
    question: 'How is my donation used?',
    answer:
      '92% of every donation goes directly toward procuring and distributing oxygen wellness products. The remaining 8% covers logistics, administration, and operational costs. We publish quarterly transparency reports so you can see exactly how funds are allocated.',
  },
  {
    question: 'Can I choose which families receive my donation?',
    answer:
      'While individual targeting isn\'t possible, you can choose a donation tier that corresponds to a specific impact — for example, sponsoring one child, one family for a month, or ten families. Our team ensures your contribution reaches the most vulnerable households first.',
  },
  {
    question: 'Are the products safe and clinically tested?',
    answer:
      'Yes. All our oxygen supplementation products are scientifically formulated, clinically tested, and approved by relevant health authorities. We partner with certified manufacturers and conduct regular quality checks before distribution.',
  },
  {
    question: 'Will I receive a receipt for my donation?',
    answer:
      'Absolutely. Every donor receives an official donation receipt via email within 24 hours of their contribution. Donations are eligible for tax exemption under Section 80G of the Income Tax Act (India).',
  },
  {
    question: 'How do I know my donation is making a real difference?',
    answer:
      'We send regular impact updates to our donors, including stories from families who have benefited, distribution photos, and measurable health outcomes. You can also view our live impact dashboard on the Impact page of this website.',
  },
  {
    question: 'Can corporates or organisations donate?',
    answer:
      'Yes! We welcome CSR partnerships and bulk donations from corporates, foundations, and institutions. Please visit our Donate page or contact us directly to discuss a tailored partnership that aligns with your CSR goals.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[#12B6B6] mb-4">
            Got Questions?
          </span>
          <h2 className="text-4xl font-bold text-[#111111] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-[#555555] text-lg max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about how your donation funds oxygen wellness products for families in need.
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                  isOpen
                    ? 'border-[#12B6B6]/40 shadow-md'
                    : 'border-gray-100 hover:border-[#12B6B6]/30'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer bg-white hover:bg-[#f0fafa] transition-colors duration-200"
                >
                  <span className="text-[#111111] font-semibold text-base pr-4">
                    {faq.question}
                  </span>
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                      isOpen
                        ? 'bg-[#12B6B6] text-white rotate-45'
                        : 'bg-[#f0fafa] text-[#12B6B6]'
                    }`}
                  >
                    <i className="ri-add-line text-lg"></i>
                  </span>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-7 pb-6 text-[#555555] leading-relaxed text-sm border-t border-gray-50 pt-4">
                    {faq.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center bg-[#f0fafa] rounded-2xl p-8">
          <p className="text-[#333333] font-medium mb-2">Still have questions?</p>
          <p className="text-[#555555] text-sm mb-6">
            Our team is happy to help. Reach out and we'll get back to you within 24 hours.
          </p>
          <a
            href="mailto:info@oxyaid.org"
            className="inline-flex items-center gap-2 bg-[#12B6B6] text-white font-semibold px-7 py-3 rounded-xl hover:bg-[#0ea5a5] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line"></i>
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
}
